import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { customToast } from "../../src/taskpane/components/CustomToast";
import { ContentManagerCopyMoveModeEnum } from "../../src/taskpane/components/enums/ContentManagerCopyMoveModeEnum";
import { FileTypeEnum } from "../../src/taskpane/components/enums/fileTypeEnum";
import { FolderTypeEnum } from "../../src/taskpane/components/enums/FolderTypeEnum";
import { FolderTypeIconEnum } from "../../src/taskpane/components/enums/FolderTypeIconEnum";
import { OneDriveOverWriteEnum } from "../../src/taskpane/components/enums/oneDriveOverWriteEnum";
import { renameDuplicateNames } from "../../src/taskpane/components/helpers/fileUploadHelper";
import { FolderTypeIconQuery } from "../../src/taskpane/components/helpers/folderTypeIconQuery";
import { OneDriveUrlHelper } from "../../src/taskpane/components/helpers/oneDriveUrlHelper";
import { StoreItemHelper } from "../../src/taskpane/components/helpers/storeItemHelper";
import { RecentFilesMapper } from "../../src/taskpane/components/mappers/recentFilesMapper";
import { CheckinTypes, ExchangeMailFolderUploadRequest, IFileSaveFileItem, IFileSaveResult, ListSettings } from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { DocubirdCache, sharePointInternalFileName, sharePointInternalTitleName } from "../../src/taskpane/config/constants";
import { IFileToUpload } from "../../src/taskpane/interfaces/IFileToUpload";
import { IPageSettings } from "../../src/taskpane/interfaces/IPageSettings";
import { ISharepointRelativeFileInfo } from "../../src/taskpane/interfaces/ISharepointRelativeFileInfo";
import { IStoreItem } from "../../src/taskpane/interfaces/IStoredItem";
import { ListItemCreationData } from "../../src/taskpane/interfaces/ListItemCreationData";
import { FileMetaData } from "../../src/taskpane/interfaces/props/FileMetaData";
import { SharePointInputData } from "../../src/taskpane/interfaces/props/SharePointInputData";
import { setContentManagerExchange, setContentManagerExchangeProgress, setContentManagerMoveTo } from "../../src/taskpane/redux/modules/contentManagerDirectory";
import { resetDuplicateFilesAndOverWriteSettings, setFiles, setOneDriveOverwriteOptionSelected } from "../../src/taskpane/redux/modules/file";
import { resetLocationBeforeSaving } from "../../src/taskpane/redux/modules/oneDrive";
import { addRecentItem } from "../../src/taskpane/redux/modules/recentItems";
import { setIsBusySaving, setIsCheckingForDuplicates, setIsSavingInBackground, setNeedsRedirectToHome } from "../../src/taskpane/redux/modules/savingInBackground";
import { resetWorkingDirectories } from "../../src/taskpane/redux/modules/workingDirectory";
import { DefaultValueService } from "../../src/taskpane/services/DefaultValueService";
import emailMapper from "../../src/taskpane/services/EmailMapper";
import { PersistorFactory } from "../../src/taskpane/services/factories/PersistorFactory";
import { FileService } from "../../src/taskpane/services/FileService";
import { getOneDriveRootDetails, getRelativeFileInfo } from "../../src/taskpane/services/GraphService";
import authModule from "../../utilities/AuthModule";
import { CacheHelper } from "../../utilities/CacheHelper";
import { DragAndDropHelper, IFileSystemFolder } from "../../utilities/DragAndDropHelper";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { logger } from "../../utilities/logger";
import { CategoryDisplayNames } from "../../utilities/OutlookUtils";
import savingLocationStructureModule from "../../utilities/SavingLocationStructureModule";
import { FileSystemEntryPersistor } from "../../src/taskpane/services/FileSystemEntryPersistor";
import { apiAddListItem, apiUploadExchangeMailFolder } from "../../src/taskpane/services/ApiService";
import { SaveMode } from "../../src/taskpane/components/enums/SaveModeEnum";
import { useFiles } from "../../src/taskpane/components/_v2/redux/hooks/useFiles";

export function useFileUpload() {
  const dispatch = useDispatch();
  const languageStrings = React.useContext(LanguageContext);

  // const hasDuplicateFiles = useSelector((state: RootState) => state.files.files.some(item => item.isDuplicate && item.isSelected && !item.isFolder));
  // const hasDuplicateFolders = useSelector((state: RootState) => state.files.files.some(item => item.isDuplicate && item.isSelected && item.isFolder));
  // const canOverwrite = useSelector((state: RootState) => state.files.canOverwrite);
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const mode = useSelector((state: RootState) => state.mode.mode);
  const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
  const files = useSelector((state: RootState) => state.files.files);
  // const selectedFiles = useSelector((state: RootState) => state.files.files.filter(item => item.isSelected));
  const savingLocation = useSelector((state: RootState) => state.savingLocation);
  const accountId = useSelector((state: RootState) => state.user?.account?.accountId);
  const convertEmailToPDF = useSelector((state: RootState) => state.user.settings.convertEmailToPDF);

  const userSettings = useSelector((state: RootState) => state.user);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
  const oneDriveOverwriteOption = useSelector((state: RootState) => state.files.oneDriveOverwriteOption);
  const oneDriveLocationBeforeSaving = useSelector((state: RootState) => state.oneDrive.locationBeforeSaving);
  const rootOneDriveId = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails?.driveId);
  const tenantReplaceFileNameWithGUID = useSelector((state: RootState) => state.tenantInfo?.replaceFileNameWithGUID);
  const moveToEnabled = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.moveToEnabled);
  const moveToMode = useSelector((state: RootState) => state.contentManagerDirectory.moveTo.mode);
  const exchange = useSelector((state: RootState) => state.contentManagerDirectory.exchange);
  const exchangeSelectedAccount = useSelector((state: RootState) => state.contentManagerDirectory.exchange.selectedAccount);

  const { checkPdfFiles } = useFiles();

  const [showSelectVersion, setShowSelectVersion] = React.useState(false);
  const [spHasDuplicateFiles, setSpHasDuplicateFiles] = React.useState<boolean>(false);
  const [spHasDuplicateFolders, setSpHasDuplicateFolders] = React.useState<boolean>(false);
  const [selectedCheckinType, setSelectedCheckinType] = React.useState<CheckinTypes>(CheckinTypes.minorCheckIn);
  const selectedCheckinTypeRef = React.useRef<CheckinTypes>(CheckinTypes.minorCheckIn);

  const [_isFileSystemFileUpload, _setIsFileSystemFileUpload] = React.useState<boolean>();
  const isFileSystemFileUploadRef = React.useRef<boolean>(false);
  const setIsFileSystemFileUpload = (isFileSystemFileUpload: boolean) => {
    isFileSystemFileUploadRef.current = isFileSystemFileUpload;
    _setIsFileSystemFileUpload(isFileSystemFileUpload);
  };

  const filesRef = React.useRef<IFileToUpload[]>([]);
  const selectedFilesRef = React.useRef<IFileToUpload[]>([]);
  React.useEffect(() => {
    filesRef.current = files;
    selectedFilesRef.current = files.filter(item => item.isSelected);
  }, [files]);

  React.useEffect(() => {
    selectedCheckinTypeRef.current = selectedCheckinType;
  }, [selectedCheckinType])

  const saveEntireEmailFolder = async (
    autoFolderCreation: boolean,
    inputValuesToSave: SharePointInputData[],
    isInOneDrive: boolean,
    folderName: string
  ) => {
    const siteUrl = !isInOneDrive ? currentDirectory?.webUrl : oneDriveRootDetails?.sharePointSiteUrl;
    const saveLocation = savingLocation ?? currentDirectory;
    const sharePointListId = !isInOneDrive ? currentDirectory.sharePointListId : oneDriveRootDetails?.sharePointListId;
    const sharePointToken = !isInOneDrive ? await authModule.getSharePointAccessToken() : await authModule.getOneDriveAccessToken();

    const emailMappings = CacheHelper.get(DocubirdCache.spEmailPropertyMappings);

    dispatch(setIsBusySaving({ isBusySaving: true, isSuccess: false }));
    dispatch(setContentManagerMoveTo({
      moveToEnabled: false,
      workingDirectories: [],
      selectedFiles: [],
      siteUrl: "",
      sourceServerRelPath: "",
    }));

    try {
      const request: ExchangeMailFolderUploadRequest = {
        sharePointToken,
        // mailToken: await authModule.getMailboxAccessToken(true),
        mailToken: await authModule.getGraphAccessToken(),
        siteUrl,
        listId: sharePointListId,
        destinationUrl: !saveLocation.directory ? saveLocation.webUrl : `${saveLocation.webUrl}/${saveLocation.directory}`,
        autoFolderCreation,
        exchangeAccountEmail: exchangeSelectedAccount,
        mailItemCategory: getCategoryType(getFolderType()),
        mailFolderId: exchange.selectedFolderId,
        metadata: inputValuesToSave.map(inputValue => {
          return {
            ...inputValue,
          }
        }),
        sharePointEmailMappings: emailMappings,
        folderName
      }

      const uploadResults = await apiUploadExchangeMailFolder(request);
      dispatch(setContentManagerExchangeProgress(uploadResults));
      dispatch(setContentManagerExchange({ selectedFolderId: "", selectedFolderName: "", selectedAccount: "" }));

      CacheHelper.remove(DocubirdCache.spEmailPropertyMappings);
    } catch (error) {
      let message = error.error ? error.error : languageStrings.ErrorMessageGeneric;
      dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: [message] }));
      logger(error, "useFileUpload.tsx (uploadExchangeMailFolder)");
    }
  }

  const saveSharePointEmailandMetadata = async (
    autoFolderCreation: boolean,
    inputValuesToSave: SharePointInputData[],
    informationProtectionId: string,
    fileMetaData?: FileMetaData[],
    setIsCMMultipleFilesMetaDataPanelOpen?: (isOpen: boolean) => void,
    savingIndividualFile?: boolean,
    setSelectedIndividualFile?: (fileId: string, isSaving: boolean, saveSuccess: boolean, saveError: boolean) => void,
    optionsSelected?: boolean,
    saveMode?: SaveMode
  ) => {
    const fileMetaDataToValidate: FileMetaData[] = [];
    if (fileMetaData) {
      fileMetaData.forEach(fileMetaData => {
        const file = selectedFilesRef.current.find(x => x.id === fileMetaData.fileId);
        if (file) {
          fileMetaDataToValidate.push({
            fileId: fileMetaData.fileId,
            name: fileMetaData.name,
            isSelected: fileMetaData.isSelected,
            metaData: fileMetaData.metaData?.filter(x => x.internalName !== sharePointInternalFileName)
          });
        }
      });
    }

    const updatedSelectedFiles = renameDuplicateNames([...selectedFilesRef.current].filter(f => !f.isFolder));
    const hasDuplicateFolders: boolean = selectedFilesRef.current?.some(x => x.isDuplicate && x.isFolder);
    let hasDuplicateFiles: boolean;

    let metaDataInvalid: boolean = false;
    if (fileMetaDataToValidate?.length > 0) {
      metaDataInvalid = !sharepointDataListIsValid(fileMetaDataToValidate, savingIndividualFile);
    }
    else {
      metaDataInvalid = !sharepointDataIsValid(inputValuesToSave);
    }

    if (metaDataInvalid) {
      dispatch(setIsSavingInBackground(false));
      if (savingIndividualFile) {
        setSelectedIndividualFile(fileMetaDataToValidate.find(x => x.isSelected === true)?.fileId, false, false, false);
      }
      return false;
    }
    else if (saveMode === SaveMode.SaveAsPdf) {
      const fileItemsToCheck: IFileToUpload[] = filesRef.current
        .map(file => {
          return updatedSelectedFiles.find(x => x.id === file.id) ?? file;
        });
      const fileCheckResults: IFileToUpload[] = await checkPdfFiles(saveMode, fileItemsToCheck);
      selectedFilesRef.current = fileCheckResults?.filter(file => file.isSelected);
      hasDuplicateFiles = fileCheckResults?.some(item => item.isSelected && item.isDuplicate && !item.isFolder);
      if (oneDriveOverwriteOption === OneDriveOverWriteEnum.notSelected && fileCheckResults?.some(res => res.isDuplicate || res.filePreCommitResult?.checkedOut || res.filePreCommitResult?.locked)) {
        dispatch(setIsSavingInBackground(false));
        setSpHasDuplicateFiles(true);
        return false;
      }
    }

    if (savingIndividualFile) {
      const individualFile = fileMetaDataToValidate.find(x => x.isSelected === true);
      setSelectedIndividualFile(individualFile.fileId, true, false, false);

      hasDuplicateFiles = selectedFilesRef.current?.some(x => x.id === individualFile.fileId && x.isDuplicate && !x.isFolder);
    } else {
      // dispatch(setIsSavingInBackground(true, ComponentTypes.SharePoint));
      hasDuplicateFiles = selectedFilesRef.current?.some(x => x.isDuplicate && !x.isFolder);
    }

    if (fileMetaDataToValidate?.length > 0) {
      const individualFile = fileMetaDataToValidate.find(x => x.isSelected === true);

      if (currentSharepointFolder?.enableMinorVersions === true && !optionsSelected) {
        setShowSelectVersion(true);
        setSpHasDuplicateFiles(hasDuplicateFiles);
        setSpHasDuplicateFolders(hasDuplicateFolders);
        dispatch(setIsSavingInBackground(false));
        if (savingIndividualFile) {
          setSelectedIndividualFile(individualFile.fileId, false, false, false);
        }
        return false;
      }
      else if ((hasDuplicateFiles || hasDuplicateFolders) && oneDriveOverwriteOption === OneDriveOverWriteEnum.notSelected) {
        setSpHasDuplicateFiles(hasDuplicateFiles);
        setSpHasDuplicateFolders(hasDuplicateFolders);
        dispatch(setIsSavingInBackground(false));
        if (savingIndividualFile) {
          setSelectedIndividualFile(individualFile.fileId, false, false, false);
        }
        return false;
      }

      if (!savingIndividualFile && setIsCMMultipleFilesMetaDataPanelOpen) {
        setIsCMMultipleFilesMetaDataPanelOpen(false);
      }
    }
    else {
      if (currentSharepointFolder?.enableMinorVersions === true && showSelectVersion === false) {
        setShowSelectVersion(true);
        setSpHasDuplicateFiles(hasDuplicateFiles);
        setSpHasDuplicateFolders(hasDuplicateFolders);
        dispatch(setIsSavingInBackground(false));
        return false;
      }
      else if (hasDuplicateFiles && oneDriveOverwriteOption === OneDriveOverWriteEnum.notSelected) {
        setSpHasDuplicateFiles(hasDuplicateFiles);
        setSpHasDuplicateFolders(hasDuplicateFolders);
        dispatch(setIsSavingInBackground(false));
        return false;
      }
      else if (hasDuplicateFolders && !spHasDuplicateFolders) {
        setSpHasDuplicateFiles(hasDuplicateFiles);
        setSpHasDuplicateFolders(hasDuplicateFolders);
        dispatch(setIsSavingInBackground(false));
        return false;
      }
    }

    if (!savingIndividualFile) {
      dispatch(setIsSavingInBackground(true, ComponentTypes.SharePoint));
      dispatch(setIsBusySaving({ isBusySaving: true, isSuccess: false }));
    }
    setShowSelectVersion(false);
    setSpHasDuplicateFiles(false);
    setSpHasDuplicateFolders(false);
    setIsFileSystemFileUpload(DragAndDropHelper.isDroppedModeActive);

    try {
      const selectedItems = CacheHelper.get(DocubirdCache.selectedExchangeItems);

      const fileMetaDataInput: FileMetaData[] = fileMetaDataToValidate?.length > 0
        ? fileMetaDataToValidate
        : updatedSelectedFiles.map(file => {
          const selectedItem = (moveToEnabled && moveToMode === ContentManagerCopyMoveModeEnum.CopySelectedEmails) ? selectedItems?.find(x => x.id === file.graphMailItemId) : null;
          return {
            fileId: file.id,
            name: file.name,
            isSelected: false,
            metaData: inputValuesToSave.filter(x => x.internalName !== sharePointInternalFileName).map(inputValue => {
              let value = (inputValue.internalName === sharePointInternalTitleName && inputValue.value === languageStrings.TitleAutoSet) ? file.name : inputValue.value;

              if (selectedItem) {
                if (inputValue.xml && inputValue.xml.DisplayName) {
                  const colName = inputValue.xml.DisplayName.trim().toLowerCase(); //directly from sharepoint
                  const spColumnNames = emailMapper.getAllListedColumns(); //list of all saved columns we should check for mapping
                  const spColumnName = spColumnNames.find(col => col.toLowerCase() === colName); //either returns undefined or a sharepoint column name 
                  if (spColumnName) {
                    const mappedResult = emailMapper.getOfficeJSToEmailMapping(spColumnName, selectedItem);
                    if (mappedResult.mappedVal?.value) {
                      value = DefaultValueService.setMappedDefault(inputValue, mappedResult.mappedVal);
                    }
                  }
                }
              }

              return {
                ...inputValue,
                value
              }
            })
          }
        });

      let fileSaveResult: IFileSaveResult;

      if (moveToEnabled) {
        dispatch(setContentManagerMoveTo({
          moveToEnabled: false,
          workingDirectories: [],
          selectedFiles: [],
          siteUrl: "",
          sourceServerRelPath: "",
        }));
        dispatch(setContentManagerExchange({ selectedFolderId: "", selectedFolderName: "", selectedAccount: "" }));
        dispatch(setIsBusySaving({ isBusySaving: true, isSuccess: false }));
      }
      else if (DragAndDropHelper.isDroppedModeActive) {
        const folderEntriesToUpload: IFileSystemFolder[] = [...DragAndDropHelper.folderEntries];
        const fileSystemFilesUploadResult = await DragAndDropHelper.uploadDroppedFiles(accountId, updatedSelectedFiles, folderEntriesToUpload);
        if (fileSystemFilesUploadResult.success === false) {
          dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: fileSystemFilesUploadResult.failedFileNames }));
          setShowSelectVersion(false);
          setSpHasDuplicateFiles(false);
          setSpHasDuplicateFolders(false);
          return false;
        }

        // const siteUrl: string = savingLocation.webUrl;
        // const parentUrl: string = `${siteUrl}${savingLocation.directory ? `/${savingLocation.directory}` : ''}`;
        // await DragAndDropHelper.createFolders(await authModule.getSharePointAccessToken(), siteUrl, parentUrl, savingLocation.sharePointListId, autoFolderCreation, updatedSelectedFiles);

        try {
          fileSaveResult = await new FileSystemEntryPersistor().saveToSharepoint(
            false,
            savingIndividualFile ? updatedSelectedFiles.filter(x => x.id === fileMetaDataToValidate[0].fileId) : updatedSelectedFiles,
            folderEntriesToUpload,
            savingLocation,
            autoFolderCreation,
            fileMetaDataInput,
            accountId,
            informationProtectionId,
            userSettings.tenantOcrLanguage,
            tenantReplaceFileNameWithGUID,
            null,
            selectedCheckinTypeRef.current,
            oneDriveOverwriteOption === OneDriveOverWriteEnum.rename,
            saveMode
          );
        } catch (error) {
          logger(error, "useFileUpload.tsx (saveSharePointEmailandMetadata - FileSystemEntryPersistor.saveToSharepoint)");
        }
      }

      const folderType = getFolderType();
      if (!isFileSystemFileUploadRef.current) {
        const persistor = PersistorFactory.create(null, null, mode, isFileSystemFileUploadRef.current);
        const listSettings: ListSettings = {
          enableMinorVersions: currentSharepointFolder.enableMinorVersions ? currentSharepointFolder.enableMinorVersions : false,
        }

        fileSaveResult = await persistor.saveToSharepoint(
          savingIndividualFile ? updatedSelectedFiles.filter(x => x.id === fileMetaDataToValidate[0].fileId) : updatedSelectedFiles,
          savingLocation,
          autoFolderCreation,
          fileMetaDataInput,
          accountId,
          informationProtectionId,
          null,
          folderType,
          false,
          userSettings.convertEmailToPDF,
          userSettings.tenantOcrLanguage,
          userSettings.tenantEnablePII,
          tenantReplaceFileNameWithGUID,
          listSettings,
          selectedCheckinTypeRef.current,
          oneDriveOverwriteOption === OneDriveOverWriteEnum.rename ? true : false,
          exchangeSelectedAccount,
          getCategoryType(folderType),
          saveMode
        );
      }

      if (savingIndividualFile) {
        const savingSuccess = fileSaveResult.success === true;
        const savingError = fileSaveResult.success === false;
        const individualFile = fileMetaDataToValidate.find(x => x.isSelected === true);
        setSelectedIndividualFile(individualFile.fileId, false, savingSuccess, savingError);
      }
      else {
        if (fileSaveResult.success === true) {
          if (Array.isArray(fileSaveResult.succeededFiles) && fileSaveResult.succeededFiles.length > 0) {
            const recentFolder = saveToSharepointRecent(folderType);
            const recentFiles = await savesFileToRecent(await buildFilesToGetRecent(null, fileSaveResult.succeededFiles), autoFolderCreation);
            dispatch(addRecentItem(accountId, [...recentFiles, ...recentFolder]));
          }
          dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: true }));
        }
        else if (fileSaveResult.success === false) {
          dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: fileSaveResult.failedFileNames }));
        }
        else {
          dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: [languageStrings.ErrorMessageGeneric] }));
        }
      }

      setShowSelectVersion(false);
      setSpHasDuplicateFiles(false);
      setSpHasDuplicateFolders(false);
      dispatch(setOneDriveOverwriteOptionSelected(OneDriveOverWriteEnum.notSelected));
    } catch (err) {
      dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: err?.response?.data }));
      setShowSelectVersion(false);
      setSpHasDuplicateFiles(false);
      setSpHasDuplicateFolders(false);
    }

    return true;
  };

  const getCategoryType = (folderType: FolderTypeEnum) => {
    let categoryToSet = "";
    switch (folderType) {
      case FolderTypeEnum.teamChannel:
        categoryToSet = CategoryDisplayNames.Teams;
        break;
      case FolderTypeEnum.sharepoint:
        categoryToSet = CategoryDisplayNames.SharePoint;
        break;
      case FolderTypeEnum.oneDrive:
        categoryToSet = CategoryDisplayNames.OneDrive;
        break;
      default:
        break;
    }

    return categoryToSet;
  }

  const addListItem = async (data: ListItemCreationData) => {
    try {
      dispatch(setIsSavingInBackground(true));
      dispatch(setIsBusySaving({ isBusySaving: true, isSuccess: false }));

      await apiAddListItem(data);

      dispatch(setIsSavingInBackground(false));
      dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: true }));
    } catch (error) {
      logger(error, "useFileUpload.tsx (addListItem)");

      dispatch(setIsSavingInBackground(false));
      dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: [languageStrings.ErrorMessageGeneric] }));
    }

    return true;
  }

  const sharepointDataIsValid = (inputValuesToSave: SharePointInputData[]) => {
    let itemsWithErrors = inputValuesToSave.filter(item => item.hasError).map(item => item.displayName);

    if (itemsWithErrors.length > 0) {
      generateToastError(itemsWithErrors);
      return false;
    }

    //double check validations
    inputValuesToSave.forEach(inputValue => {
      if (inputValue.xml?.Required === "TRUE") {
        if (!inputValue.value) {
          itemsWithErrors.push(inputValue.displayName);
        }
        else if (Array.isArray(inputValue.value) && inputValue.value?.length === 0) {
          itemsWithErrors.push(inputValue.displayName);
        }
        else if (Array.isArray(inputValue.value?.terms) && inputValue.value?.terms?.length === 0) {
          itemsWithErrors.push(inputValue.displayName);
        }
      }
    });

    if (itemsWithErrors.length > 0) {
      generateToastError(itemsWithErrors);
      return false
    }

    return true;
  };

  const sharepointDataListIsValid = (fileMetaData: FileMetaData[], savingIndividualFile: boolean) => {
    let allErrors: string[] = [];

    fileMetaData.forEach(file => {
      if (savingIndividualFile && file.isSelected) {
        const itemsWithErrors = file.metaData.filter(item => item.hasError).map(item => `${item.displayName} (${file.name})`);
        allErrors = allErrors.concat(itemsWithErrors);
      }

      if (!savingIndividualFile) {
        const itemsWithErrors = file.metaData.filter(item => item.hasError).map(item => `${item.displayName} (${file.name})`);
        allErrors = allErrors.concat(itemsWithErrors);
      }

    });

    if (allErrors.length > 0) {
      generateToastError(allErrors);
    }

    return allErrors.length === 0;
  };

  const sharepointDataListEntireFolderIsValid = (inputValues: SharePointInputData[]) => {
    let allErrors: string[] = [];

    const itemsWithErrors = inputValues.filter(item => item.hasError).map(item => `${item.displayName}`);
    allErrors = allErrors.concat(itemsWithErrors);

    if (allErrors.length > 0) {
      generateToastError(allErrors);
    }

    return allErrors.length === 0;
  };

  const generateToastError = (items: string[]) => {
    customToast.error(
      <div>
        <strong>{languageStrings.MetadataValidationFailed}</strong><br />
        {generateErrors(items)}
      </div>
    );
  };

  const generateErrors = (items: string[]) => {
    return items.map((item, index) => {
      return (
        <span key={index}>
          &bull; {item} <br />
        </span>
      );
    });
  };

  const getFolderType = () => {
    if (currentDirectory.isTeamsRootFolder) return FolderTypeEnum.teamChannel;
    if (currentDirectory.type === ComponentTypes.OneDrive)
      return FolderTypeEnum.oneDrive;

    return FolderTypeEnum.sharepoint;
  };

  const saveToSharepointRecent = (folderType: FolderTypeEnum.sharepoint | FolderTypeEnum.oneDrive | FolderTypeEnum.teamChannel): IStoreItem[] => {
    try {
      const savinglocationStructure = savingLocationStructureModule.getSavingLocationStructure();
      const lastDirectoryItem = savinglocationStructure ? savinglocationStructure[savinglocationStructure.length - 1] : null;
      const item: IStoreItem = {
        driveId: !lastDirectoryItem?.parentId ? lastDirectoryItem.id : lastDirectoryItem?.parentId,
        locations: savinglocationStructure && [...savinglocationStructure],
        itemId: !lastDirectoryItem?.parentId ? null : lastDirectoryItem?.id,
        name: lastDirectoryItem.fileTitle,
        fileType: FileTypeEnum.folder,
        folderTypeIcon: FolderTypeIconQuery.getIconType(
          folderType,
          {
            driveId: !lastDirectoryItem?.parentId ? lastDirectoryItem.id : lastDirectoryItem?.parentId,
            id: !lastDirectoryItem?.parentId ? null : lastDirectoryItem.id,
            name: "",
            contentTypeId: lastDirectoryItem?.contentTypeId
          },
          currentDirectory?.isTeamsFolder
        ),
        datePinned: new Date(),
        folderId: !lastDirectoryItem?.parentId ? null : lastDirectoryItem?.id,
        sharePointListId: lastDirectoryItem?.sharePointListId
      };

      StoreItemHelper.removeSaveLocation(item.locations);
      if (item.locations?.length > 0) {
        return [item];
      }
    } catch (e) {
      logger(e, "useFileUpload.tsx (saveToSharepointRecent)");
    }
    return [];
  };

  const savesFileToRecent = async (files: ISharepointRelativeFileInfo[], autoFolderCreation: boolean): Promise<IStoreItem[]> => {
    const data = await getRelativeFileInfo(files, autoFolderCreation); //info of files being saved
    if (!data.length) {
      return [];
    }

    try {
      const mapper = new RecentFilesMapper(languageStrings);
      return mapper.map(data, true);
    } catch (e) {
      logger(e, "App.tsx (savesFileToRecent)");
    }
    return [];
  }

  const buildFilesToGetRecent = async (fileSavedLocation?: IPageSettings, succeededFiles?: IFileSaveFileItem[]): Promise<ISharepointRelativeFileInfo[]> => {
    const fileLocation: IPageSettings = fileSavedLocation ?? savingLocationStructureModule.getSavingDestination() ?? currentDirectory;
    /*
      Required due to inconsistent property casing.
    */
    let fileDocumentLibrary: string = fileLocation.DocumentLibrary;
    if (!fileDocumentLibrary) {
      try {
        fileDocumentLibrary = fileLocation["documentLibrary"];
      } catch (error) {
        logger(error, "App.tsx (buildFilesToGetRecent)");
      }
    }

    const driveId: string = await determineDriveIdForSaving(fileLocation);
    return selectedFilesRef.current.map((email, index) => ({
      driveId: driveId,
      documentLibrary: fileDocumentLibrary,
      directory: fileLocation.directory,
      fileName: `${succeededFiles[index] ? succeededFiles[index].name : `${email.name}.${email.extension}`}`
    }));
  }

  const determineDriveIdForSaving = async (destinationLocation: IPageSettings) => {
    const destinationLocationType = destinationLocation?.type;
    return destinationLocationType == ComponentTypes.OneDrive || destinationLocationType == ComponentTypes.OneDriveSubFolder
      ? getOneDriveBusinessDriveId(destinationLocation)
      : getSharepointDriveId(destinationLocation);
  }

  const getSharepointDriveId = (destinationLocation: IPageSettings) => {
    return !destinationLocation?.parentId ? destinationLocation.id : destinationLocation.parentId;
  }

  const getOneDriveBusinessDriveId = async (destinationLocation: IPageSettings) => {
    return destinationLocation.type === ComponentTypes.OneDrive ? oneDriveRootDetails?.driveId ?? (await getOneDriveRootDetails())?.driveId : destinationLocation.parentId;
  }

  const saveToOneDrive = async (
    folderName: string = "",
    fromLocationItemCurrentDirectory?: IPageSettings,
    savingLocationDirectory?: string,
    locationBeforeSaving?: IPageSettings[],
    searchedFolderLocation?: IPageSettings[],
    goToHome: boolean = false,
    saveMode?: SaveMode
  ): Promise<void> => {

    dispatch(setIsCheckingForDuplicates(true));
    try {
      const duplicateFileResult = await getDuplicateOneDriveResult(saveMode);

      await doOneDriveSavingWork(
        folderName,
        duplicateFileResult,
        goToHome,
        fromLocationItemCurrentDirectory,
        savingLocationDirectory,
        locationBeforeSaving,
        searchedFolderLocation,
        saveMode
      );
    } catch (error) {
      logger(error, "CMUploadFilesPanel.tsx (saveToOneDrive)");
      dispatch(setIsCheckingForDuplicates(false));
    }
  };

  const getDuplicateOneDriveResult = async (saveMode?: SaveMode): Promise<IFileToUpload[]> => {
    const savinglocationStructure = savingLocationStructureModule.getSavingLocationStructure();
    const lastDirectoryItem = savinglocationStructure ? savinglocationStructure[savinglocationStructure.length - 1] : null;
    const directoryUrl = !lastDirectoryItem?.directory
      ? lastDirectoryItem?.webUrl
      : `${lastDirectoryItem?.webUrl}${lastDirectoryItem?.directory?.startsWith('/') ? lastDirectoryItem.directory : `/${lastDirectoryItem?.directory}`}`;

    return await FileService.getFilePreCommitCheck(
      filesRef.current,
      {
        autoFolderCreation: false,
        destinationUrl: directoryUrl,
        siteUrl: OneDriveUrlHelper.getSiteUrl(lastDirectoryItem?.webUrl)
      },
      convertEmailToPDF,
      saveMode
    );
  };

  const doOneDriveSavingWork = async (
    folderName: string,
    duplicateFileResult: IFileToUpload[],
    goToHome: boolean,
    fromLocationItemCurrentDirectory?: IPageSettings,
    savingLocationDirectory?: string,
    locationBeforeSaving?: IPageSettings[],
    searchedFolderLocation?: IPageSettings[],
    saveMode?: SaveMode
  ): Promise<void> => {
    dispatch(setFiles(duplicateFileResult));
    dispatch(setIsCheckingForDuplicates(false));

    const duplicateFiles = duplicateFileResult.some(item => item.isDuplicate && !item.isFolder);
    const duplicateFolders = duplicateFileResult.some(item => item.isDuplicate && item.isFolder);

    if (duplicateFiles || duplicateFolders) {
      if (duplicateFiles && (!oneDriveOverwriteOption || oneDriveOverwriteOption === OneDriveOverWriteEnum.notSelected)) {
        setSpHasDuplicateFiles(true);
        dispatch(setOneDriveOverwriteOptionSelected(OneDriveOverWriteEnum.rename));

        if (duplicateFolders && !spHasDuplicateFolders) {
          setSpHasDuplicateFolders(true);
        }

        if (goToHome) dispatch(setNeedsRedirectToHome(goToHome));
        return;
      }
      if (duplicateFolders && !spHasDuplicateFolders) {
        if (goToHome) dispatch(setNeedsRedirectToHome(goToHome));
        setSpHasDuplicateFolders(true);
        return;
      }
    }

    dispatch(setIsBusySaving({ isBusySaving: true, isSuccess: false }));
    dispatch(setIsSavingInBackground(true, ComponentTypes.OneDrive));

    setIsFileSystemFileUpload(DragAndDropHelper.isDroppedModeActive);

    const updatedSelectedFiles = renameDuplicateNames([...selectedFilesRef.current].filter(f => !f.isFolder));
    const folderType = getFolderType();

    let fileSaveResult: IFileSaveResult;
    try {
      if (moveToEnabled) {
        dispatch(setContentManagerMoveTo({
          moveToEnabled: false,
          workingDirectories: [],
          selectedFiles: [],
          siteUrl: "",
          sourceServerRelPath: "",
        }));
        dispatch(setContentManagerExchange({ selectedFolderId: "", selectedFolderName: "", selectedAccount: "" }));
        dispatch(setIsBusySaving({ isBusySaving: true, isSuccess: false }));
        CacheHelper.remove(DocubirdCache.selectedExchangeItems);
      }
      else if (DragAndDropHelper.isDroppedModeActive) {
        const folderEntriesToUpload: IFileSystemFolder[] = [...DragAndDropHelper.folderEntries];
        const fileSystemFilesUploadResult = await DragAndDropHelper.uploadDroppedFiles(accountId, updatedSelectedFiles, folderEntriesToUpload);
        if (fileSystemFilesUploadResult.success === false) {
          dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: fileSystemFilesUploadResult?.failedFileNames }));
          return;
        }

        // const oneDriveDetails: IOneDriveRootDetails = oneDriveRootDetails ?? await getOneDriveRootDetails();
        // const savingDestination = savingLocationStructureModule.getSavingDestination();
        // const directory = savingLocationDirectory ? savingLocationDirectory : savingDestination?.directory;
        // const parentUrl = !directory
        //   ? savingDestination?.webUrl
        //   : `${savingDestination?.webUrl}${directory.startsWith('/') ? directory : `/${directory}`}`;
        // await DragAndDropHelper.createFolders(await authModule.getOneDriveAccessToken(), oneDriveDetails?.sharePointSiteUrl, parentUrl, oneDriveDetails?.sharePointListId, false, selectedFiles);
        try {
          fileSaveResult = await new FileSystemEntryPersistor().saveToSharepoint(
            true,
            updatedSelectedFiles,
            folderEntriesToUpload,
            savingLocation,
            false,
            [],
            accountId,
            null,
            userSettings.tenantOcrLanguage,
            false,
            null,
            CheckinTypes.majorCheckIn,
            oneDriveOverwriteOption === OneDriveOverWriteEnum.rename,
            saveMode
          );
        } catch (error) {
          logger(error, "useFileUpload.tsx (doOneDriveSavingWork - FileSystemEntryPersistor.saveToSharepoint)");
        }
      }

      if (!isFileSystemFileUploadRef.current) {
        const persistor = PersistorFactory.create(null, null, mode, isFileSystemFileUploadRef.current);
        fileSaveResult = await persistor.saveToOneDrive(
          updatedSelectedFiles,
          savingLocationDirectory ? savingLocationDirectory : savingLocation.directory,
          oneDriveOverwriteOption,
          null,
          savingLocationDirectory ? null : folderName,
          accountId,
          false,
          false,
          userSettings.tenantOcrLanguage,
          exchangeSelectedAccount,
          getCategoryType(folderType)
        );
      }

      if (fileSaveResult.success === true) {
        if (Array.isArray(fileSaveResult.succeededFiles) && fileSaveResult.succeededFiles.length > 0) {
          const recentFolder = saveToOneDriveRecent();
          const recentFiles = await savesFileToRecent(await buildFilesToGetRecent(fromLocationItemCurrentDirectory, fileSaveResult.succeededFiles), false);
          addRecentItem(accountId, [...recentFiles, ...recentFolder]);
        }
        dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: true }));
        resetToPreviousOneDriveLocationBeforeSaving(locationBeforeSaving ?? oneDriveLocationBeforeSaving);
        dispatch(resetDuplicateFilesAndOverWriteSettings(filesRef.current));
      }
      else if (fileSaveResult.success === false) {
        dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: fileSaveResult.failedFileNames }));
      }
      else {
        dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false, errorMessages: [languageStrings.ErrorMessageGeneric] }));
      }
    } catch (err) {
      logger(err, "CMUploadFilesPanel.txs (doOneDriveSavingWork)");
      customToast.error(languageStrings.ErrorMessageGeneric, { autoClose: false });
      dispatch(setIsBusySaving({ isBusySaving: false, isSuccess: false }));
    }

    setSpHasDuplicateFolders(false);
    setSpHasDuplicateFiles(false);

    if (goToHome) {
      dispatch(resetWorkingDirectories());
      dispatch(setIsSavingInBackground(false));
      dispatch(resetDuplicateFilesAndOverWriteSettings(filesRef.current));
      dispatch(setNeedsRedirectToHome(false));
    }
  };

  const saveToOneDriveRecent = (): IStoreItem[] => {
    try {
      const savinglocationStructure = savingLocationStructureModule.getSavingLocationStructure();
      const lastDirectoryItem = savinglocationStructure ? savinglocationStructure[savinglocationStructure.length - 1] : null;
      const item: IStoreItem = {
        driveId: !lastDirectoryItem?.parentId ? rootOneDriveId : lastDirectoryItem?.parentId,
        locations: savinglocationStructure && [...savinglocationStructure],
        itemId: !lastDirectoryItem?.parentId ? null : lastDirectoryItem?.id,
        name: !lastDirectoryItem?.parentId ? ComponentTypes.OneDrive : lastDirectoryItem.fileTitle,
        fileType: FileTypeEnum.folder,
        folderTypeIcon: lastDirectoryItem?.type === ComponentTypes.OneDrive ? FolderTypeIconEnum.oneDrive : FolderTypeIconEnum.oneDriveSubFolder,
        datePinned: new Date(),
        folderId: !lastDirectoryItem?.parentId ? null : lastDirectoryItem?.id
      };
      StoreItemHelper.removeSaveLocation(item.locations);
      //recentMode.save(item);
      if (item.locations?.length > 0) {
        return [item];
      }
    } catch (error) {
      logger(error, "App.tsx (saveToOneDriveRecent)");
    }
    return [];
  };

  const resetToPreviousOneDriveLocationBeforeSaving = (previousLocation: IPageSettings[]) => {
    if (previousLocation?.length > 0) {
      dispatch(resetWorkingDirectories(previousLocation));
      dispatch(resetLocationBeforeSaving());
    }
  };

  return {
    saveSharePointEmailandMetadata,
    saveToOneDrive,
    showSelectVersion,
    setShowSelectVersion,
    setSelectedCheckinType,
    addListItem,
    spHasDuplicateFiles,
    setSpHasDuplicateFiles,
    spHasDuplicateFolders,
    setSpHasDuplicateFolders,
    saveEntireEmailFolder,
    sharepointDataListEntireFolderIsValid
  }
}